import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { TShirtIcon as TShirt, ArrowsClockwiseIcon as ArrowsClockwise, PlugIcon as Plug, PlugsConnectedIcon as PlugsConnected, WarningIcon as Warning, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, TrashIcon as Trash } from "@phosphor-icons/react";

/**
 * Per-seller Printify integration on /me/store → Merch tab.
 *
 * Seller pastes their own Printify API key + we validate by listing
 * their shops, then they pick one. Every shop sync pulls all visible
 * products into `store_products` as `kind=merch` so they show up on
 * their public /store/{slug} page right alongside food.
 *
 * Order fulfillment back to Printify is manual for now — the seller
 * gets the order in their normal Dog Shop orders queue and pushes it
 * to Printify themselves. (Auto-fulfillment is risky without per-
 * seller shipping-method + address-validation defaults.)
 */
export default function PrintifyTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [shopsAvailable, setShopsAvailable] = useState(null);

  const load = () => {
    api.get("/me/store/printify/status")
      .then((r) => setStatus(r.data))
      .catch((e) => {
        toast.error(formatApiError(e));
        setStatus({ connected: false, synced_count: 0 });
      });
  };
  useEffect(load, []);

  const connect = async () => {
    if (!apiKey.trim() || apiKey.trim().length < 20) {
      toast.error("Paste your full Printify API key.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/me/store/printify/connect", { api_key: apiKey.trim() });
      setShopsAvailable(data.shops_available || null);
      toast.success(`Connected — selected shop: ${data.shop_name}`);
      setApiKey("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/store/printify/sync");
      toast.success(`Synced ${data.synced} product${data.synced === 1 ? "" : "s"} (${data.skipped} skipped)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      "Disconnect Printify? Your synced merch products will be hidden "
      + "(not deleted — order history stays intact). You can reconnect any time."
    )) return;
    setBusy(true);
    try {
      await api.post("/me/store/printify/disconnect");
      toast.success("Printify disconnected.");
      setShopsAvailable(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoFulfill = async (next) => {
    setBusy(true);
    try {
      await api.post("/me/store/printify/auto-fulfill", { enabled: next });
      toast.success(next ? "Auto-fulfillment ON — paid merch orders push to Printify automatically." : "Auto-fulfillment OFF — push orders manually from the Orders tab.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div className="py-6 text-sm text-[#6B6B6B]">Loading…</div>;

  return (
    <div className="py-6 space-y-6" data-testid="my-store-printify-tab">
      <header>
        <div className="flex items-center gap-3">
          <TShirt size={22} weight="duotone" className="text-gold-dark" />
          <h2 className="font-spectral text-2xl tracking-tight">Print-on-demand merch</h2>
        </div>
        <p className="text-[13px] text-[#6B6B6B] mt-1 max-w-2xl">
          Connect your own Printify account and we&rsquo;ll mirror your designs into
          your storefront as merch products. You stay the merchant of record —
          Armstrong K9 Registry is just the technology and payment host.
        </p>
      </header>

      {!status.connected ? (
        <ConnectForm
          apiKey={apiKey}
          setApiKey={setApiKey}
          busy={busy}
          onConnect={connect}
        />
      ) : (
        <>
          <section
            className="rounded-2xl border border-[#BFE8C9] bg-[#EEFBEC] p-5"
            data-testid="printify-connected-card"
          >
            <div className="flex items-start gap-3">
              <PlugsConnected size={22} weight="fill" className="text-[#0F8043] mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-spectral text-lg tracking-tight">
                  Connected to {status.shop_name || "Printify"}
                </h3>
                <p className="text-[12.5px] text-[#3F3F3F] mt-0.5">
                  Shop ID <code className="font-tabular">{status.shop_id}</code>
                  {" · "}
                  <strong>{status.synced_count}</strong> product
                  {status.synced_count === 1 ? "" : "s"} synced
                  {status.last_synced_at && (
                    <> · last sync {new Date(status.last_synced_at).toLocaleString()}</>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Button
                onClick={sync}
                disabled={busy}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="printify-sync-btn"
              >
                <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
                {busy ? "Syncing…" : "Sync now"}
              </Button>
              <Button
                onClick={disconnect}
                disabled={busy}
                variant="outline"
                className="rounded-full border-crimson/40 text-crimson hover:bg-crimson hover:text-white"
                data-testid="printify-disconnect-btn"
              >
                <Trash size={13} weight="bold" className="mr-1.5" /> Disconnect
              </Button>
              <a
                href="https://printify.com/app"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 text-[12.5px] text-[#6B6B6B] hover:text-ink underline ml-auto self-center"
              >
                Manage on Printify <ArrowSquareOut size={11} weight="bold" />
              </a>
            </div>
          </section>

          <AutoFulfillCard
            enabled={!!status.auto_fulfill}
            onToggle={toggleAutoFulfill}
            busy={busy}
          />
        </>
      )}

      {shopsAvailable && shopsAvailable.length > 1 && (
        <section className="rounded-xl border border-[#EAEAEA] bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#6B6B6B] mb-2">
            Multiple shops on this account
          </p>
          <ul className="space-y-1">
            {shopsAvailable.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between text-[13px] py-1.5 border-b border-[#F0F0F0] last:border-0"
              >
                <span>
                  <strong>{s.title}</strong>
                  <span className="text-[#9A9A9A] font-tabular ml-2">#{s.id}</span>
                </span>
                {String(s.id) === String(status?.shop_id) ? (
                  <span className="text-[11px] text-[#0F8043] flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" /> Active
                  </span>
                ) : (
                  <span className="text-[11px] text-[#9A9A9A]">
                    To switch, disconnect then reconnect with this shop ID
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ConnectForm({ apiKey, setApiKey, busy, onConnect }) {
  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-5 max-w-2xl">
      <div className="flex items-start gap-3 mb-4">
        <Plug size={20} weight="duotone" className="text-[#6B6B6B] mt-0.5" />
        <div>
          <h3 className="font-spectral text-lg tracking-tight">Connect your Printify account</h3>
          <p className="text-[12.5px] text-[#6B6B6B] mt-1">
            One-time setup. You keep your own Printify account; the Registry just
            mirrors the products into your storefront so buyers can check out
            without leaving.
          </p>
        </div>
      </div>

      <ol className="space-y-2 text-[12.5px] text-[#3a3a3a] mb-5 border-l-2 border-gold pl-4">
        <li>
          Open <a href="https://printify.com/app/account/api" target="_blank" rel="noreferrer noopener" className="underline">
            Printify → My profile → Connections
          </a>{" "}
          (or sign up if you don&rsquo;t have an account &mdash; it&rsquo;s free).
        </li>
        <li>Click <strong>Generate</strong> next to &ldquo;Personal access token&rdquo;.</li>
        <li>Give it a name like &ldquo;Armstrong K9 Registry storefront&rdquo;.</li>
        <li>Select <strong>all scopes</strong> (we need shops + products read).</li>
        <li>Copy the long token that appears once.</li>
        <li>Paste it below and click <strong>Connect</strong>.</li>
      </ol>

      <div>
        <Label className="text-xs">Printify personal access token</Label>
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9…"
          className="font-tabular text-[12px]"
          data-testid="printify-api-key-input"
          autoComplete="off"
        />
        <p className="text-[10.5px] text-[#9A9A9A] mt-1">
          Stored privately on your store record. Never shown back to you in full.
          You can disconnect any time.
        </p>
      </div>

      <Button
        onClick={onConnect}
        disabled={busy || !apiKey.trim()}
        className="mt-4 rounded-full bg-ink hover:bg-crimson text-white"
        data-testid="printify-connect-btn"
      >
        <Plug size={14} weight="bold" className="mr-1.5" />
        {busy ? "Validating…" : "Connect Printify"}
      </Button>
    </section>
  );
}

function FulfillmentNote() {
  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="printify-fulfillment-note">
      <Warning size={16} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
      <div className="text-[12.5px] text-amber-900 leading-relaxed">
        <strong>Heads-up on order fulfillment.</strong> When a buyer pays for a
        merch product on your storefront, the order lands in your{" "}
        <strong>Dog Shop Orders tab</strong> but isn&rsquo;t automatically
        pushed into Printify. Open the order, copy the variant +
        address, and create the Printify order manually so you control
        shipping method and any final design tweaks. We&rsquo;ll add
        auto-fulfillment in a future update.
      </div>
    </section>
  );
}

function AutoFulfillCard({ enabled, onToggle, busy }) {
  return (
    <section
      className="rounded-2xl border border-[#EAEAEA] bg-white p-5"
      data-testid="printify-auto-fulfill-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="font-spectral text-lg tracking-tight">Auto-fulfillment</h3>
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-xl leading-relaxed">
            When ON, every paid merch order on your storefront is pushed to
            Printify automatically using the buyer&rsquo;s shipping address and the
            first enabled variant. When OFF, orders land in your Orders tab
            and you push them with the &ldquo;Send to Printify&rdquo; button.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={busy}
          onClick={() => onToggle(!enabled)}
          data-testid="printify-auto-fulfill-toggle"
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-50 ${enabled ? "bg-ink" : "bg-[#D7D7D7]"}`}
        >
          <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${enabled ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
        <Warning size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-[12px] text-amber-900 leading-relaxed">
          <strong>Heads-up.</strong> Auto-fulfill picks the <em>first</em>{" "}
          enabled variant of each product. If you sell multiple sizes/colors
          and want size-by-size routing, leave this OFF and push orders
          manually. Errors surface on the Orders tab with a retry button.
        </p>
      </div>
    </section>
  );
}
